import React, { useState, useEffect, useCallback } from 'react';
import styled, { keyframes } from 'styled-components';
import whatsappService from '../../services/whatsappService';
import authService from '../../services/authService';

// Animações
const pulse = keyframes`
  0% { opacity: 0.6; transform: scale(0.98); }
  50% { opacity: 1; transform: scale(1); }
  100% { opacity: 0.6; transform: scale(0.98); }
`;

const fadeIn = keyframes`
  from { opacity: 0; }
  to { opacity: 1; }
`;

// Componentes estilizados
const QRCodeContainer = styled.div`
  position: relative;
  width: 100%;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  margin: 1rem 0;
  animation: ${fadeIn} 0.5s ease-out;
`;

const QRCodeWrapper = styled.div`
  position: relative;
  width: 220px;
  height: 220px;
  background: #fff;
  border-radius: 12px;
  padding: 10px;
  margin: 1rem auto;
  overflow: hidden;
  box-shadow: 0 5px 15px rgba(0, 0, 0, 0.2);
  display: flex;
  justify-content: center;
  align-items: center;
  border: 2px solid rgba(100, 182, 255, 0.5);
`;

const QRCodeImage = styled.img`
  width: 100%;
  height: 100%;
  object-fit: contain;
  pointer-events: none;
`;

const LoadingContainer = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  height: 220px;
  width: 220px;
  margin: 1rem auto;
  background: rgba(0, 0, 0, 0.05);
  border-radius: 10px;
  animation: ${pulse} 1.5s infinite ease-in-out;
`;

const LoadingSpinner = styled.div`
  width: 40px;
  height: 40px;
  border: 3px solid rgba(0, 100, 255, 0.1);
  border-top: 3px solid rgba(0, 100, 255, 0.8);
  border-radius: 50%;
  animation: spin 1s linear infinite;
  margin-bottom: 1rem;
  
  @keyframes spin {
    0% { transform: rotate(0deg); }
    100% { transform: rotate(360deg); }
  }
`;

const StatusMessage = styled.div`
  margin-top: 1rem;
  text-align: center;
  font-size: 0.9rem;
  color: ${props => props.error ? '#ff3860' : props.success ? '#23d160' : '#4a4a4a'};
  max-width: 300px;
  line-height: 1.4;
`;

const InstructionsContainer = styled.div`
  position: relative;
  margin-top: 1.5rem;
  width: 100%;
  max-width: 340px;
  background: linear-gradient(135deg, rgba(0, 20, 40, 0.9), rgba(0, 40, 80, 0.9));
  border-radius: 16px;
  padding: 22px;
  box-shadow: 0 10px 30px rgba(0, 0, 0, 0.3);
  border: 1px solid rgba(100, 182, 255, 0.3);
  backdrop-filter: blur(10px);
`;

const InstructionTitle = styled.div`
  font-weight: 600;
  font-size: 1.1rem;
  margin-bottom: 20px;
  color: #64B6FF;
  text-shadow: 0 0 15px rgba(100, 182, 255, 0.5);
  letter-spacing: 0.5px;
  text-align: center;
`;

const StepsContainer = styled.div`
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  margin: 15px 0;
  padding: 0 5px;
`;

const StepRow = styled.div`
  display: flex;
  align-items: center;
  margin: 8px 0;
  width: 100%;
`;

const StepNumber = styled.div`
  width: 26px;
  height: 26px;
  border-radius: 50%;
  background: rgba(100, 182, 255, 0.2);
  color: #64B6FF;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 0.8rem;
  font-weight: 600;
  margin-right: 12px;
  border: 1px solid rgba(100, 182, 255, 0.3);
`;

const StepText = styled.span`
  color: ${props => props.highlight ? '#FFFFFF' : '#E0E0E0'};
  font-weight: ${props => props.highlight ? '600' : '500'};
  font-size: 0.95rem;
  letter-spacing: 0.3px;
`;

const Divider = styled.div`
  height: 1px;
  width: 100%;
  background: linear-gradient(90deg, transparent, rgba(100, 182, 255, 0.3), transparent);
  margin: 18px 0;
`;

const InfoText = styled.div`
  text-align: center;
  font-size: 0.9rem;
  color: #ffffff;
  letter-spacing: 0.3px;
  line-height: 1.5;
  padding: 0 5px;
`;

const SuccessContainer = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  height: 220px;
  width: 220px;
  margin: 1rem auto;
  background: rgba(35, 209, 96, 0.05);
  border-radius: 10px;
  border: 1px solid rgba(35, 209, 96, 0.2);
`;

const SuccessIcon = styled.div`
  width: 60px;
  height: 60px;
  background: rgba(35, 209, 96, 0.1);
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  margin-bottom: 1rem;
  
  &::before {
    content: '✓';
    font-size: 2rem;
    color: #23d160;
  }
`;

const ErrorContainer = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  height: 220px;
  width: 220px;
  margin: 1rem auto;
  background: rgba(255, 56, 96, 0.05);
  border-radius: 10px;
  border: 1px solid rgba(255, 56, 96, 0.2);
`;

const ErrorIcon = styled.div`
  width: 60px;
  height: 60px;
  background: rgba(255, 56, 96, 0.1);
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  margin-bottom: 1rem;
  
  &::before {
    content: '!';
    font-size: 2rem;
    color: #ff3860;
  }
`;

const RetryButton = styled.button`
  margin-top: 1rem;
  padding: 0.5rem 1rem;
  background: rgba(0, 100, 255, 0.1);
  color: rgba(0, 100, 255, 0.8);
  border: 1px solid rgba(0, 100, 255, 0.3);
  border-radius: 4px;
  cursor: pointer;
  transition: all 0.3s ease;
  
  &:hover {
    background: rgba(0, 100, 255, 0.2);
  }
`;

const WhatsAppQRCode = () => {
  const [qrCode, setQrCode] = useState(null);
  const [status, setStatus] = useState('loading'); // loading, pending, connected, error
  const [errorMessage, setErrorMessage] = useState('');
  const [webhookCount, setWebhookCount] = useState(0);
  const [currentQrCode, setCurrentQrCode] = useState(null);

  // Obter o telefone do usuário do localStorage
  const getUserPhone = useCallback(() => {
    const userData = authService.getUserData();
    if (userData && userData.telefone) {
      return userData.telefone;
    }
    return localStorage.getItem('userPhone');
  }, []);

  // Função para verificar o status da conexão
  const checkConnectionStatus = useCallback(async () => {
    try {
      const phoneNumber = getUserPhone();
      
      if (!phoneNumber) {
        setStatus('error');
        setErrorMessage('Número de telefone não encontrado. Por favor, faça login novamente.');
        return;
      }
      
      const result = await whatsappService.checkConnectionStatus(phoneNumber);
      
      if (result.status === 'connected') {
        setStatus('connected');
        setQrCode(null);
        setCurrentQrCode(null);
      } else if (result.qrCode) {
        setStatus('pending');
        
        // Verifica se é um novo QR code
        if (result.qrCode !== currentQrCode) {
          setQrCode(result.qrCode);
          setCurrentQrCode(result.qrCode);
        }
      } else {
        setStatus('error');
        setErrorMessage('Não foi possível obter o QR code. Por favor, tente novamente.');
      }
    } catch (error) {
      console.error('Erro ao verificar status da conexão:', error);
      setStatus('error');
      setErrorMessage('Ocorreu um erro ao verificar a conexão. Por favor, tente novamente.');
    }
  }, [getUserPhone, currentQrCode]);

  // Efeito para verificar o status inicial
  useEffect(() => {
    checkConnectionStatus();
  }, [checkConnectionStatus]);

  // Efeito para verificar o status a cada 10 segundos
  useEffect(() => {
    let intervalId;
    
    if (status === 'loading' || status === 'pending') {
      intervalId = setInterval(() => {
        checkConnectionStatus();
        setWebhookCount(prev => prev + 1);
        
        // Limitar a 12 verificações (2 minutos)
        if (webhookCount >= 11) {
          setStatus('error');
          setErrorMessage('O QR code expirou. Por favor, tente novamente.');
          clearInterval(intervalId);
        }
      }, 10000);
    }
    
    return () => {
      if (intervalId) {
        clearInterval(intervalId);
      }
    };
  }, [status, webhookCount, checkConnectionStatus]);

  // Função para tentar novamente
  const handleRetry = () => {
    setStatus('loading');
    setErrorMessage('');
    setWebhookCount(0);
    setQrCode(null);
    setCurrentQrCode(null);
    checkConnectionStatus();
  };

  // Renderiza o conteúdo com base no status
  const renderContent = () => {
    switch (status) {
      case 'loading':
        return (
          <LoadingContainer>
            <LoadingSpinner />
            <span>Carregando QR Code...</span>
          </LoadingContainer>
        );
      
      case 'pending':
        return (
          <QRCodeWrapper>
            <QRCodeImage 
              src={qrCode.startsWith('data:image') ? qrCode : `data:image/png;base64,${qrCode}`}
              alt="QR Code WhatsApp" 
            />
          </QRCodeWrapper>
        );
      
      case 'connected':
        return (
          <SuccessContainer>
            <SuccessIcon />
            <span>WhatsApp conectado com sucesso!</span>
          </SuccessContainer>
        );
      
      case 'error':
        return (
          <ErrorContainer>
            <ErrorIcon />
            <span>{errorMessage || 'Erro ao conectar WhatsApp'}</span>
            <RetryButton onClick={handleRetry}>Tentar Novamente</RetryButton>
          </ErrorContainer>
        );
      
      default:
        return null;
    }
  };

  return (
    <QRCodeContainer>
      {renderContent()}
      
      {status === 'pending' && (
        <InstructionsContainer>
          <InstructionTitle>Como escanear o QR Code:</InstructionTitle>
          
          <StepsContainer>
            <StepRow>
              <StepNumber>1</StepNumber>
              <StepText>Abra o WhatsApp</StepText>
            </StepRow>
            
            <StepRow>
              <StepNumber>2</StepNumber>
              <StepText>Menu/Configurações</StepText>
            </StepRow>
            
            <StepRow>
              <StepNumber>3</StepNumber>
              <StepText highlight>Dispositivos conectados</StepText>
            </StepRow>
          </StepsContainer>
          
          <Divider />
          
          <InfoText>
            Nesse WhatsApp que a BIANCA vai enviar as mensagens para os leads.
          </InfoText>
        </InstructionsContainer>
      )}
      
      {status === 'loading' && (
        <StatusMessage>
          Preparando conexão com WhatsApp...
        </StatusMessage>
      )}
      
      {status === 'connected' && (
        <StatusMessage success>
          Seu WhatsApp está conectado à BIANCA.
        </StatusMessage>
      )}
      
      {status === 'error' && (
        <StatusMessage error>
          {errorMessage}
        </StatusMessage>
      )}
    </QRCodeContainer>
  );
};

export default WhatsAppQRCode;
